import {BatteryChargeValue, FuelLevelValue, Parameter} from 'platform/components';
import {NumberFormatter, useFormatNumber} from 'platform/locale';

import {isNil, isNotNil, reject} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {GetRentalOrderApiResponse} from '@dms/api/rental';
import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {GetRentalVehicleApiResponse} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {formatBatteryCharge, useCodeList} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, buildArray} from 'shared/web';

import {getRentalOrderBatteryCharge} from '../../../utils/getRentalOrderBatteryCharge';
import {getRentalOrderFuelTankLevel} from '../../../utils/getRentalOrderFuelTankLevel';
import {getRentalOrderFuelTankRatio} from '../../../utils/getRentalOrderFuelTankRatio';
import {MAX_FUEL_TANK_RATIO} from '../../../utils/parseFuelTankRatio';

export function useRentalOrderHeaderParameters(
  vehicle: GetVehicleApiResponseV2 | Nullish,
  rentalOrder: GetRentalOrderApiResponse | Nullish,
  rentalVehicle: GetRentalVehicleApiResponse | Nullish
): Parameter[] {
  const formatNumber = useFormatNumber();

  const [carParkData] = useCodeList('carPark');
  const {data: userData} = useGetCurrentUserInfoQuery();

  const selectedCarParkName = carParkData?.find(
    (carPark) => carPark.codeId === rentalVehicle?.carParkId
  )?.name;

  if (isNil(rentalOrder) || isNil(rentalVehicle)) {
    return [
      `${i18n.t('general.labels.createdBy')}: ${reject(isNilOrEmpty, [userData?.firstName, userData?.lastName]).join(' ')}`,
    ];
  }

  const fuelTank = getRentalOrderFuelTankLevel(rentalOrder);
  const fuelTankRatio = getRentalOrderFuelTankRatio(rentalOrder);
  const batteryCharge = getRentalOrderBatteryCharge(rentalOrder);

  return buildArray<Parameter>()
    .when(isNotNil(vehicle?.publicId), {
      title: vehicle?.publicId || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.publicId'),
    })
    .add({
      title: vehicle?.vin || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.vin'),
    })
    .add({
      title: vehicle?.registrationPlate || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
    })
    .add({
      title: selectedCarParkName || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.selectedCarPark'),
    })
    .add({
      title:
        formatMileage(formatNumber, rentalOrder?.rentVehicle?.actualMileage) || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.actualMileage'),
    })
    .whenNotNil(fuelTank, (level) => ({
      title: level,
      tooltip: i18n.t('entity.workshop.labels.fuelTank'),
      icon: 'specification/fuel_pump_thin',
      isIconNotFilled: true,
      variant: 'secondary',
      content: <FuelLevelValue value={fuelTankRatio} max={MAX_FUEL_TANK_RATIO} tone="neutral" />,
    }))
    .whenNotNil(batteryCharge, (charge) => ({
      title: formatBatteryCharge(charge),
      tooltip: i18n.t('entity.vehicle.labels.batteryCharge'),
      icon: 'electricalParams/bolt',
      variant: 'secondary',
      content: <BatteryChargeValue value={charge} tone="neutral" />,
    }));
}

const formatMileage = (formatNumber: NumberFormatter, mileage: number | Nullish) => {
  const formatedMileage = formatNumber(mileage);

  if (!formatedMileage) {
    return EMPTY_PLACEHOLDER;
  }

  return `${formatedMileage} ${i18n.t('general.metric.km')}`;
};
